import { useState } from 'react'
import { Link } from 'react-router-dom'

import { TextField } from './Fields'
import { Logo } from '../LandingPage/Logo'
import { SlimLayout } from './SlimLayout'
import TwoFactorVerification from './TwoFactorVerification'

import {
	requestEmailCode,
	verifyEmailCode,
	acceptEmailCodeTerms,
} from '../../services/authService'

const STEP_EMAIL = 'email'
const STEP_CODE = 'code'
const STEP_TERMS = 'terms'

function errorMessage(err, fallback) {
	return err?.response?.data?.error || fallback
}

/**
 * Signs in without a password, and creates the account if the address turns out
 * not to have one. The server decides which of the two it was, so this screen
 * never has to ask — and never reveals — whether someone is already registered.
 *
 * The terms step is only reachable once a code has come back, so an address is
 * proven before anything is created and the branch leaks nothing.
 */
export default function EmailCodeSignIn({
	onCancel,
	onSuccess,
	sourceExtension,
	extensionOnly,
	userToken,
}) {
	const [step, setStep] = useState(STEP_EMAIL)
	const [email, setEmail] = useState('')
	const [pending, setPending] = useState(null)
	const [consentToken, setConsentToken] = useState(null)
	const [agreed, setAgreed] = useState(false)
	const [errorText, setErrorText] = useState('')
	const [loading, setLoading] = useState(false)

	const emailLooksValid = /^\S+@\S+\.\S+$/.test(email.trim())

	const sendCode = async () => {
		if (!emailLooksValid || loading) return

		setLoading(true)
		setErrorText('')

		try {
			const data = await requestEmailCode({ email: email.trim() })
			setPending(data)
			setStep(STEP_CODE)
		} catch (err) {
			setErrorText(errorMessage(err, 'Could not send the code. Please try again.'))
		} finally {
			setLoading(false)
		}
	}

	// A resend mints a fresh pending token, so the new one replaces the old
	// before the OTP screen submits against it.
	const resendCode = async () => {
		const data = await requestEmailCode({ email: email.trim() })
		setPending(data)
	}

	const handleVerified = data => {
		if (data?.requiresTerms) {
			setConsentToken(data.consentToken)
			setStep(STEP_TERMS)
			return
		}
		onSuccess(data)
	}

	const finishSignUp = async () => {
		if (!agreed || loading) return

		setLoading(true)
		setErrorText('')

		try {
			const data = await acceptEmailCodeTerms({
				consentToken,
				sourceExtension,
				extensionOnly,
				userToken,
			})
			onSuccess(data)
		} catch (err) {
			setErrorText(errorMessage(err, 'Could not finish sign-up. Please try again.'))
		} finally {
			setLoading(false)
		}
	}

	if (step === STEP_CODE) {
		return (
			<TwoFactorVerification
				pendingLoginToken={pending.pendingLoginToken}
				maskedEmail={pending.maskedEmail}
				title="Enter your code"
				onVerify={args =>
					verifyEmailCode({ ...args, sourceExtension, extensionOnly, userToken })
				}
				onResend={resendCode}
				onCancel={onCancel}
				onSuccess={handleVerified}
			/>
		)
	}

	return (
		<SlimLayout>
			<div className="flex">
				<Logo color={'text-gray-600 text-lg'} height={'45px'} width={'45px'} />
			</div>

			{step === STEP_EMAIL ? (
				<>
					<h2 className="mt-4 mb-2 text-lg font-semibold text-gray-900">
						Sign in with a code
					</h2>
					<p className="text-sm text-gray-600 mb-6">
						Enter your email and we'll send you a 6-digit code. No password needed.
					</p>

					<TextField
						id="email-code-address"
						type="email"
						placeholder="you@company.com"
						autoComplete="email"
						autoFocus
						value={email}
						onChange={e => setEmail(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && sendCode()}
						className="mb-4"
					/>

					{errorText && (
						<div className="error-message text-red-500 text-sm mb-4">
							{errorText}
						</div>
					)}

					<button
						type="button"
						onClick={sendCode}
						disabled={!emailLooksValid || loading}
						className="w-full py-2.5 px-4 text-sm font-semibold bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
					>
						{loading && (
							<span className="loading loading-spinner loading-md mr-2"></span>
						)}
						Send code
					</button>
				</>
			) : (
				<>
					<h2 className="mt-4 mb-2 text-lg font-semibold text-gray-900">
						One last thing
					</h2>
					<p className="text-sm text-gray-600 mb-6">
						You don't have an account yet — accept the terms to create one.
					</p>

					<label className="flex items-start gap-3 mb-4 cursor-pointer">
						<input
							type="checkbox"
							checked={agreed}
							onChange={e => setAgreed(e.target.checked)}
							className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
						/>
						<span className="text-sm text-gray-600">
							I agree to the{' '}
							<Link
								to="/terms"
								target="_blank"
								className="font-medium text-blue-600 hover:underline"
							>
								Terms &amp; Conditions
							</Link>{' '}
							and{' '}
							<Link
								to="/policy"
								target="_blank"
								className="font-medium text-blue-600 hover:underline"
							>
								Privacy Policy
							</Link>
						</span>
					</label>

					{errorText && (
						<div className="error-message text-red-500 text-sm mb-4">
							{errorText}
						</div>
					)}

					<button
						type="button"
						onClick={finishSignUp}
						disabled={!agreed || loading}
						className="w-full py-2.5 px-4 text-sm font-semibold bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
					>
						{loading && (
							<span className="loading loading-spinner loading-md mr-2"></span>
						)}
						Create my account
					</button>
				</>
			)}

			<button
				type="button"
				onClick={onCancel}
				className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer"
			>
				Back to sign in
			</button>
		</SlimLayout>
	)
}
